import React, { Suspense, useMemo } from 'react';
import { collages } from '../../data';
import './CollagesPage.css';
import StaticNoiseCollageCard from './components/StaticNoiseCollageCard';
import { isWebGLAvailable } from './cloth/webgl';
import { getImagePaths, getBestImagePath, shouldUseThumbnail } from '../../utils/imageUtils';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import useReducedMotion from '../../hooks/useReducedMotion';

/* three.js is ~118KB gzipped and is needed on this route alone, so it
   travels in its own chunk rather than in the initial bundle. */
const ClothGallery = React.lazy(() => import('./components/ClothGallery'));

const CollagesPage: React.FC = () => {
    useDocumentMeta({
        title: 'Collages',
        description:
            'Collages stitched together from second-hand books, flea-market photographs and found paper.'
    });

    const reducedMotion = useReducedMotion();
    // Probing costs a throwaway canvas, so it happens once per mount rather
    // than on every render.
    const hasWebGL = useMemo(() => isWebGLAvailable(), []);

    // The gallery is a full-window stage and renders its own heading, so it
    // deliberately sits outside the padded page container.
    if (hasWebGL) {
        return (
            <Suspense
                fallback={
                    <p className="collages-loading" role="status">
                        unrolling…
                    </p>
                }
            >
                <ClothGallery collages={collages} reducedMotion={reducedMotion} />
            </Suspense>
        );
    }

    // Without WebGL there is no cloth to hang, so the grid is still the page
    // rather than an error message.
    return (
        <div className="collages-container">
            <div className="collages-main-content">
                <div className="collages-header">
                    <h1>Collages</h1>
                    <p className="collages-intro">
                        {collages.length} pieces. Each one lists the books it was cut from.
                    </p>
                </div>

                <div className="collages-grid">
                    {collages.map(collage => {
                        const paths = getImagePaths(collage.images.main);
                        const imagePath = getBestImagePath(paths, shouldUseThumbnail('grid'));
                        return (
                            <StaticNoiseCollageCard
                                key={collage.id}
                                id={collage.id}
                                title={collage.title}
                                date={collage.date}
                                image={imagePath}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CollagesPage;
